/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import React, {Component} from 'react';
import uuid from 'uuid';
import ChevronDown from '@react/react-spectrum/Icon/ChevronDown';
import Button from '@react/react-spectrum/Button';
import Link from '@react/react-spectrum/Link';
import Heading from '@react/react-spectrum/Heading';
import {getLocalizedURL} from '@intl/adobe-locales';
import CCDownloadPageScss from './CCDownloadPage.scss';
import InlineFeedback from 'cchome-shared/components/InlineFeedback/InlineFeedback';
import AppsUtil from '../../../../utils/AppsUtil';
import {EVENT_TYPE} from '../../../../utils/CCDAnalyticsUtils';
import CCDUtil from '../../../../utils/CCDUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import URLUtils from '../../../../utils/Url';
import CSSUtil from '../../../../utils/CSSUtil';
import DownloadUtil from '../../../../utils/DownloadUtil';
import DeviceDetectionUtil from '../../../../utils/DeviceDetectionUtil';
import getConfigApps from '../../../../config';
import DownloadCoachMark from '../../../../components/DownloadCoachMark/DownloadCoachMark';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import AppsAnalytics from '../../../../analytics/AppsAnalytics';
import strings from '../../../../../translations/en.json';

type State = {
	shouldHideDownloadInstructionsCTA: boolean
};

type Props = {
	productDetails: Object,
	isDataAvailable: boolean,
	intl: Object,
	environment: string
};

export default class CCDownloadPage extends Component<Props, State> {
	productId: string;
	product: Object;
	features: Array<Object>;

	constructor(props: Props) {
		super(props);
		this.productId = this.props.productDetails.id;
		/**
		 * state variable set to true if download was initiated
		 * by user on front door page or if user landed from
		 * an adobe website
		*/
		this.state = {
			shouldHideDownloadInstructionsCTA: this.shouldAutoDownload()
		};
		// if FFC data is available before component mount
		if (this.props.isDataAvailable) {
			this.product = AppsUtil.getFFCProductConfig(this.productId);
		}
		this.features = [
			{
				id: 'install',
				bgImageSrc: '/assets/ccd/images/Apps-still.jpg',
				bgVideoSrcM4v: '/assets/ccd/images/Apps-animation.m4v',
				bgVideoSrcMp4: '/assets/ccd/images/Apps-animation.mp4'
			},
			{
				id: 'discover',
				bgImageSrc: '/assets/ccd/images/Discover-still.jpg',
				bgVideoSrcM4v: '/assets/ccd/images/Discover-animation.m4v',
				bgVideoSrcMp4: '/assets/ccd/images/Discover-animation.mp4'
			},
			{
				id: 'assets',
				bgImageSrc: '/assets/ccd/images/Your-work-still.jpg',
				bgVideoSrcM4v: '/assets/ccd/images/Your-work-animation.m4v',
				bgVideoSrcMp4: '/assets/ccd/images/Your-work-animation.mp4'
			},
			{
				id: 'marketplace',
				bgImageSrc: '/assets/ccd/images/Marketplace-still.jpg',
				bgVideoSrcM4v: '/assets/ccd/images/Marketplace-animation.m4v',
				bgVideoSrcMp4: '/assets/ccd/images/Marketplace-animation.mp4'
			}
		];
		CCDUtil.downloadCC = this.downloadCC;
	}

	componentDidMount() {
		const wrapperElement = document.getElementsByClassName('cc-download-scrollable-page')[0];
		if (wrapperElement) {
			wrapperElement.style.backgroundColor = '#fff';
		}
		// if FFC data is available before component mount
		if (this.props.isDataAvailable) {
			this.shouldAutoDownload() && this.downloadCC({
				shouldSkipAnimation: true,
				subType: 'auto'
			});
			this.handleAnalyticEvents(EVENT_TYPE.RENDER);
			this.disableVideoRightClick();
		}
		// for SEO
		document.title = this.props.intl.formatMessage({id: 'apps.cc.download.page.title'});
		const metas = document.getElementsByTagName('meta');
		for (let i = 0; i < metas.length; i++) {
			// update description tag
			if (metas[i].getAttribute('name') === 'description') {
				metas[i].content = this.props.intl.formatMessage({id: 'apps.cc.download.page.description'});
			}
			// remove robot meta tag
			if (AppsUtil.hasFeature('temp_remove_robots_ccd_page')
				&& metas[i].getAttribute('name') === 'robots') {
				metas[i].parentNode
					&& metas[i].parentNode.removeChild(metas[i]);
			}
		}
		// supported langs in cchome-apps are different (zh-hans/zh-hant not present)
		const supportedLangs = Object.keys(getConfigApps('SUPPORTED_LANGUAGES'));
		const addLinkTag = (rel, href, hreflang) => {
			const link = document.createElement('link');
			link.rel = rel;
			link.href = href;
			if (hreflang) {
				link.hreflang = hreflang;
			}
			document.head
				&& document.head.appendChild(link);
		};
		supportedLangs.forEach((lang) => {
			addLinkTag('alternate', `https://creativecloud.adobe.com/${lang}/apps/download/creative-cloud`, lang);
		});
	}

	componentWillUpdate(nextProps: Object) {
		// Assigning this.product to FFC product config here to avoid duplication
		if (this.props.isDataAvailable !== nextProps.isDataAvailable) {
			this.product = AppsUtil.getFFCProductConfig(this.productId);
		}
	}

	componentDidUpdate(prevProps: Object) {
		if (prevProps.isDataAvailable !== this.props.isDataAvailable) {
			this.shouldAutoDownload() && this.downloadCC({
				shouldSkipAnimation: true,
				subType: 'auto'
			});
			this.handleAnalyticEvents(EVENT_TYPE.RENDER);
			this.disableVideoRightClick();
		}
	}

	disableVideoRightClick() {
		const videos = document.querySelectorAll('video');
		const videoArr = Array.prototype.slice.call(videos, 0);
		videoArr.forEach((video) => {
			video.oncontextmenu = () => false;
		});
	}

	componentWillUnmount() {
		const wrapperElement = document.getElementsByClassName('cc-download-scrollable-page')[0];
		if (wrapperElement) {
			wrapperElement.style.backgroundColor = 'initial';
		}
	}

	handleAnalyticEvents(type: string, eventObj: Object = {}) {
		let event = {
			workflow: 'CC Downloads',
			subcategory: 'Download'
		};
		let content = {
			id: this.product.id,
			name: this.product.displayName,
			version: CompatibilityUtil.getSupportedAppVersion(this.product.id)
		};
		let ui = {
			view_type: 'Trailhead' // eslint-disable-line camelcase
		};
		const env = {
			name: URLUtils.getParam('svcName'),
			version: URLUtils.getParam('svcVer'),
			comName: URLUtils.getParam('comName'),
			comVer: URLUtils.getParam('comVer'),
		};
		if (eventObj.content) {
			content = {
				...eventObj.content,
				...content
			};
		}
		if (eventObj.event) {
			event = {
				...eventObj.event,
				...event
			};
		}
		let value;
		switch (type) {
			case EVENT_TYPE.RENDER:
				value = 'welcome';
				if (this.shouldAutoDownload()) {
					value = 'instructions';
				}
				event = {
					...event,
					type,
					subtype: 'page',
					value
				};
				break;
			case EVENT_TYPE.CLICK:
				event = {
					...event,
					type
				};
				break;
			case EVENT_TYPE.FEEDBACK:
				if (eventObj.content) {
					content = {
						...content,
						...eventObj.content
					};
				}
				if (eventObj.event) {
					event = {
						...event,
						...eventObj.event
					};
				}
				break;
			default:
		}
		if (eventObj.ui) {
			ui = {
				...eventObj.ui,
				...ui
			};
		}
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		analytics.genericAnalyticEvent({event, content, ui, env});
	}

	// Is landing from adobe website or on tall screen
	shouldAutoDownload = () => {
		if (!AppsUtil.isMobileOrTab()
			&& (!this.redirectedFrom(['/apps/download/creative-cloud', 'adobeid', AppsUtil.getCCHostName()]) // to prevent autodownload on page refresh/sign-in/sign-out
				&& DownloadUtil.checkInputUrlValidity(document.referrer, ['adobe.com', 'behance.net', 'substance3d.com']))) {
			return true;
		}
		if (!AppsUtil.isMobileOrTab()
			&& this.redirectedFrom([AppsUtil.getCCHostName()])
			&& !this.redirectedFrom(['/apps/download/creative-cloud'])
			&& URLUtils.getParam('comVer') === 'Trailhead'
			&& URLUtils.getParam('x-location') === 'Home') {
			return true;
		}
		return false;
	}

	redirectedFrom(urlPatterns: Array<string>) {
		const referrer = document.referrer.split('?')[0];
		return urlPatterns.find(urlPattern => referrer.indexOf(urlPattern) > -1);
	}

	handleSignIn = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(`${window.location.origin}/apps`, undefined, guid, undefined, undefined, '_self');
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'sign-in',
				guid
			},
			content: {
				action: url
			}
		});
	}

	handleViewApps = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(`${window.location.origin}/apps`, undefined, guid, undefined, undefined, '_self');
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'view-apps',
				guid
			},
			content: {
				action: url
			}
		});
	}

	onWelcomeDownloadCTAClick = () => {
		if (AppsUtil.isMobileOrTab()) {
			this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
				event: {
					subtype: 'send-link'
				}
			});
		}
		else {
			this.setState({shouldHideDownloadInstructionsCTA: true});
			this.downloadCC({
				subType: 'welcome-cta-click'
			});
			this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
				event: {
					subtype: 'download',
					value: 'welcome'
				}
			});
		}
	}

	/**
	 * Download CCD
	 * shouldSkipAnimation: to skip scrolling animation
	 * shouldSkipScrolling: should skip scrolling to instructions page
	 */
	downloadCC = (params: ?Object = {}) => {
		const {shouldSkipAnimation, shouldSkipScrolling, subType} = params;
		if (AppsUtil.isMobileOrTab()) {
			const parentElem = document.getElementsByClassName('app-container')[0];
			parentElem.scrollTop = 0;
			return;
		}
		if (!shouldSkipScrolling) {
			// Scroll to instructions page
			const parentElem = document.getElementsByClassName('app-container')[0];
			const targetElem = document.getElementsByClassName(CCDownloadPageScss.instructionsSection)[0];
			if (shouldSkipAnimation) {
				parentElem.scrollTop = targetElem.offsetTop - 148;
			}
			else {
				const targetPos = targetElem.offsetTop - 148;
				CSSUtil.animateScrollToElem(25, targetPos, parentElem, 1);
			}
		}
		DownloadUtil.downloadProduct({sapcode: this.props.productDetails.sapCode, hasThor:false, checkCompatability:false ,isAllApps:true});
		CSSUtil.addAnimatedInstallerPopup(<DownloadCoachMark ccDownloadPage={true} intl={this.props.intl}/>, 1500, false, () => {}, true);
		const appsAnalytics = new AppsAnalytics(this.props.environment);
		appsAnalytics.initAnalytics();
		this.product.entitled = AppsUtil.isAppEntitledNew(this.product, this.product.id, this.product.type);
		this.product.sapCode = this.product.id;
		appsAnalytics.onAppDownloadEvent(this.product, 'Trailhead', subType, 'CC Downloads');
	}

	renderWelcomeDownloadCTA = () => {
		if (AppsUtil.isMobileOrTab()) {
			const guid = uuid.v4();
			const emailHref = DownloadUtil.getEmailMeLinkHref(
				this.productId === 'creative-cloud',
				this.product.displayName,
				'',
				'ccd',
				this.props.productDetails.downloadURL,
				guid,
				this.props.intl.formatMessage
			);
			return <Button
				element='a'
				variant='cta'
				className={CCDownloadPageScss.downloadCTA}
				href={emailHref}
				label={this.props.intl.formatMessage({id: 'apps.cc.download.sendLink'})}
				ariaLabel={`${this.props.intl.formatMessage({id: 'apps.cc.download.sendLink'})}, button`}
				onClick={this.onWelcomeDownloadCTAClick} />;
		}
		return <Button
			variant='cta'
			className={CCDownloadPageScss.downloadCTA}
			label={this.props.intl.formatMessage({id: 'apps.cc.download.downloadCta'}, {appName: this.product.displayName})}
			ariaLabel={`${this.props.intl.formatMessage({id: 'apps.cc.download.downloadCta'}, {appName: this.product.displayName})}, button`}
			onClick={this.onWelcomeDownloadCTAClick} />;
	}

	renderViewAppsSection = () => {
		if (adobeIMS.isSignedInUser()) {
			return (
				<div>
					<Button
						variant='primary'
						className={`${CCDownloadPageScss.viewAppsBtn} primary-viewApps-btn`}
						label={this.props.intl.formatMessage({id: 'apps.cc.download.viewYourApps'})}
						role='button'
						ariaLabel={this.props.intl.formatMessage({id: 'apps.cc.download.viewYourApps'})}
						onClick={this.handleViewApps} />
				</div>
			);
		}
		if (AppsUtil.isMobileOrTab()) {
			return (
				<div className={CCDownloadPageScss.signInMobile}>
					{this.props.intl.formatMessage({id: 'apps.cc.download.signIn.mobile'}, {
						a: str => <Link
							className={CCDownloadPageScss.signInLink}
							ariaLabel='Sign-in, button'
							onClick={this.handleSignIn}>
							{str}
						</Link>
					})}
				</div>
			);
		}
		return (
			<>
				<div
					className={CCDownloadPageScss.signInText}>
					{this.props.intl.formatMessage({id: 'apps.cc.download.signInText'})}
				</div>
				<Button
					variant='primary'
					className={`${CCDownloadPageScss.signInBtn} primary-signin-btn`}
					label={this.props.intl.formatMessage({id: 'ccd.old.welcomeScreen.signIn'})}
					role='button'
					ariaLabel={this.props.intl.formatMessage({id: 'ccd.old.welcomeScreen.signIn'})}
					onClick={this.handleSignIn} />
			</>
		);
	}

	renderWelcomePage = () => {
		const icon = CCDUtil.getUrlPath('/assets/ccd/images/cc-mnemonic.png');
		return (
			<section className={`${CCDownloadPageScss.welcome} ${this.isMobile() ? CCDownloadPageScss.welcomeFullPage : ''}`}>
				<div className={CCDownloadPageScss.leftSection}>
					<img alt={this.product.displayName} src={icon} />
					<Heading>
						{this.props.intl.formatMessage({id: 'apps.cc.download.heading'}, {appName: this.product.displayName})}
					</Heading>
					<div className={CCDownloadPageScss.description}>
						{AppsUtil.isMobileOrTab()
							? this.props.intl.formatMessage({id: 'apps.cc.download.desc.on.mobile'}, {appName: this.product.displayName})
							: this.props.intl.formatMessage({id: 'apps.cc.download.desc'}, {appName: this.product.displayName})}
					</div>
					{this.renderWelcomeDownloadCTA()}
					{this.renderViewAppsSection()}
				</div>
				<div className={CCDownloadPageScss.rightSectionImg}
					style={{backgroundImage: `url(${CCDUtil.getUrlPath('/assets/ccd/images/Apps-still.png')})`}} />
			</section>
		);
	}

	renderFeature = (feature: Object, index: number) => <div key={index} className={`${CCDownloadPageScss.ccFeature} ${index % 2 === 1 ? CCDownloadPageScss.reverse : ''}`}>
		<div className={CCDownloadPageScss.featureMedia}>
			<video
				autoPlay
				loop
				muted
				playsInline
				poster={CCDUtil.getUrlPath(feature.bgImageSrc)}>
				<source src={feature.bgVideoSrcMp4} type='video/mp4' />
				<source src={feature.bgVideoSrcM4v} type='video/mp4' />
			</video>
		</div>
		<style>
			{
				`#feature::before {
					content: '';
					display: inline-block;
					vertical-align: middle;
					height: 100%;
				}`
			}
		</style>
		<div className={CCDownloadPageScss.overview} id='feature'>
			<div className={CCDownloadPageScss.featureWrapper}>
				<h2>
					{this.props.intl.formatMessage({id: `apps.cc.download.features.${feature.id}.heading`})}
				</h2>
				<div className={CCDownloadPageScss.desc}>
					{this.props.intl.formatMessage({id: `apps.cc.download.features.${feature.id}.desc`})}
				</div>
			</div>
		</div>
	</div>

	renderCCFeatures = () => <section className={CCDownloadPageScss.ccFeatures}>
		<h2 className={CCDownloadPageScss.ccFeaturesHeading}>
			{this.props.intl.formatMessage({id: 'apps.cc.download.features.heading'})}
		</h2>
		<div className={CCDownloadPageScss.featuresDesc}>
			{this.props.intl.formatMessage({id: 'apps.cc.download.features.desc'})}
		</div>
		{this.features.map(this.renderFeature)}
	</section>

	onRestartDownload = () => {
		this.downloadCC({
			shouldSkipScrolling: true,
			subType: 'retry'
		});
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'restart-download'
			}
		});
	};

	onGetHelp = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(getLocalizedURL('https://helpx.adobe.com/download-install.html', this.props.intl.locale), this.props.productDetails.sapCode, guid);
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'help',
				guid
			},
			content: {
				action: url
			}
		});
	};

	renderHelpSection = () => (
		<div className={CCDownloadPageScss.helpSection}>
			<span className={CCDownloadPageScss.tryAgainTitle}>
				{this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.tryAgainTitle'})}
			</span>
			<Link
				className={CCDownloadPageScss.tryAgainLink}
				target='download_frame'
				variant='quiet'
				role='button'
				ariaLabel={`${this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.tryAgainLink'})}, button`}
				onClick={this.onRestartDownload}>
				{this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.tryAgainLink'})}
			</Link>
			<span>
				{this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.or'})}
			</span>
			<span role='link' tabIndex={0} className={CCDownloadPageScss.helpLink} onClick={this.onGetHelp}
				onKeyDown={(e) => {e.keyCode === 13 && this.onGetHelp();}}>
				{this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.helpLink'})}
			</span>
		</div>
	);

	onInstructionsDownloadCTAClick = () => {
		this.downloadCC({
			shouldSkipScrolling: true,
			subType: 'instructions-cta-click'
		});
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'download',
				value: 'instructions'
			}
		});
	}

	renderInstructionsSection = () => {
		let step2Id = 'apps.cc.download.instruction.page.step2';
		if (DeviceDetectionUtil.isEdge()
			|| DeviceDetectionUtil.isInternetExplorer()) {
			step2Id = 'apps.cc.download.instruction.page.step2.ie_edge';
		}
		return (
			<section className={CCDownloadPageScss.instructionsSection}>
				<div className={CCDownloadPageScss.instructionPageHeader}>
					{this.props.intl.formatMessage({id: 'apps.cc.download.instruction.page.heading'})}
				</div>
				{!this.state.shouldHideDownloadInstructionsCTA && <Button
					variant='cta'
					className={CCDownloadPageScss.downloadCTA}
					label={this.props.intl.formatMessage({id: 'apps.cc.download.downloadCta'}, {appName: this.product.displayName})}
					ariaLabel={`${this.props.intl.formatMessage({id: 'apps.cc.download.downloadCta'}, {appName: this.product.displayName})}, button`}
					onClick={this.onInstructionsDownloadCTAClick}
				/>}
				<div className={CCDownloadPageScss.instructionSteps}>
					<div className={CCDownloadPageScss.step1}>
						<div className={CCDownloadPageScss.bulletPoint}>
							{this.props.intl.formatMessage({id: 'apps.AppPicker.kaizen.detailed_body.first_num'})}
						</div>
						<div className={CCDownloadPageScss.text}>
							{this.props.intl.formatMessage({id: `apps.cc.download.instruction.page.step1_${DeviceDetectionUtil.getBrowser() ? DeviceDetectionUtil.getBrowser() : 'other'}`},
								{filename: `${this.product.displayName.replace(/ /g, '_')}${DeviceDetectionUtil.isWin() ? '_Set-Up.exe' : '_Installer.dmg'}`})}
						</div>
					</div>
					<div className={CCDownloadPageScss.step2}>
						<div className={CCDownloadPageScss.bulletPoint}>
							{this.props.intl.formatMessage({id: 'apps.AppPicker.kaizen.detailed_body.mid_num'})}
						</div>
						<div className={CCDownloadPageScss.text}>
							{this.props.intl.formatMessage({id: step2Id})}
						</div>
					</div>
				</div>
				{this.renderHelpSection()}
			</section>
		);
	}

	onArrowDownClick = () => {
		const parentElem = document.getElementsByClassName('app-container')[0];
		const targetPos = document.getElementsByClassName(CCDownloadPageScss.ccFeatures)[0].offsetTop - 80;
		CSSUtil.animateScrollToElem(25, targetPos, parentElem, 1);
		this.handleAnalyticEvents(EVENT_TYPE.CLICK, {
			event: {
				subtype: 'scroll-down'
			}
		});
	}

	isMobile = () => DeviceDetectionUtil.isTouchDevice() && window.innerWidth < 481

	render() {
		const feedbackContext = {
			analyticsCallbackHandler: (data) => {
				this.handleAnalyticEvents(EVENT_TYPE.FEEDBACK, data.format2);
			},
			isMobileOrTabletBrowser: () => DeviceDetectionUtil.isMobileOrTab(),
			onFeedbackClick: () => {
				const parentElem = document.querySelector('.cc-download-scrollable-page');
				if (parentElem) {
					parentElem.scrollTop = parentElem.scrollHeight;
				}
			}
		};
		const analyticStrings = {
			title1: strings['inlineFeedback.title1'],
			title2: strings['inlineFeedback.title2']
		};
		return (
			this.props.isDataAvailable
				? <>
					<style>
						{
							`#cc-download-page::before {
								background-image: url(${CCDUtil.getUrlPath('/assets/ccd/images/trailhead-background.jpg')});
							}`
						}
					</style>
					<main
						id='cc-download-page'
						className={CCDownloadPageScss.wrapper}>
						{this.renderWelcomePage()}
						{!this.isMobile()
							&& <article
								tabIndex={0}
								onClick={this.onArrowDownClick}
								aria-label='Arrow Down, button'
								onKeyDown={(e) => {e.keyCode === 13 && this.onArrowDownClick();}}
								className={`${CCDownloadPageScss.chevronDownIcon} arrow-down-btn`}>
								<ChevronDown size='XXL'/>
							</article>}
						{this.renderCCFeatures()}
						{!AppsUtil.isMobileOrTab()
							&& this.renderInstructionsSection()}
						<InlineFeedback intl={this.props.intl} feedbackContext={feedbackContext} analyticStrings={analyticStrings} />
					</main>
				</>
				: null
		);
	}
}
